// Lane K — MBMR authoring: "General instructions & abbreviations" (BMR p. 9–10). Markdown textarea + editable
// abbreviation table (@/components/DataTable) + copy to sibling MBMRs. Version = MBMR version; frozen onto every
// batch issued from it. 2026-10-21 (owner): shown on the tablet before each process, merged with that process's
// own instruction (read once per person — services/execution/acknowledgement.process_note_block).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Input, Modal, Popconfirm, Select, Space, Tabs, Tag, Tooltip, Typography } from 'antd';
import { Copy, Eye, Plus, Save, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import DataTable from '@/components/DataTable';
import { ackApi } from '@/utils/ackApi';
import { marked } from 'marked';

/** Markdown → HTML for the preview (kept here: the batch acknowledgement modal it lived in is gone). */
export const renderInstructions = (text) => {
  try {
    return { __html: marked.parse(String(text || ''), { breaks: true }) };
  } catch (e) {
    return { __html: String(text || '').replace(/\n/g, '<br/>') };
  }
};

const { Text } = Typography;
// The height every tab body keeps; longer content scrolls inside it, never the page.
const BOX_H = 'min(420px, 50vh)';

export default function GeneralInstructionsPanel({ recipeId, siblingRecipes = [], readOnly = false, required = false, onStateChange }) {
  const [block, setBlock] = useState(null);
  const [text, setText] = useState('');
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTargets, setCopyTargets] = useState([]);
  // 2026-10-12 · instructions brought in from a .txt file: read in the browser, one instruction per
  // line, and only staged — they reach the record when the author presses Save, like typed text.
  const fileRef = useRef(null);
  const [uploaded, setUploaded] = useState(null);   // {name, text} waiting for Replace / Add to the end
  const MAX_TXT = 200 * 1024;
  const pickFile = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';                     // the same file may be chosen again
    if (!f) return;
    if (!/\.txt$/i.test(f.name) && f.type !== 'text/plain') { toast.error('Choose a .txt file.'); return; }
    if (f.size > MAX_TXT) { toast.error('The file is larger than 200 KB.'); return; }
    const reader = new FileReader();
    reader.onerror = () => toast.error('The file could not be read.');
    reader.onload = () => {
      const raw = String(reader.result || '').replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
      const lines = raw.split('\n').map((l) => l.replace(/\s+$/, '')).filter((l) => l.trim());
      if (!lines.length) { toast.error('The file has no text.'); return; }
      const clean = lines.join('\n');
      if (!text.trim()) { setText(clean); toast.success(`${lines.length} line(s) added from ${f.name} — press Save to keep them.`); }
      else setUploaded({ name: f.name, text: clean, count: lines.length });
    };
    reader.readAsText(f, 'utf-8');
  };
  const applyUpload = (mode) => {
    if (!uploaded) return;
    setText(mode === 'replace' ? uploaded.text : `${text.replace(/\s+$/, '')}\n${uploaded.text}`);
    toast.success(`${uploaded.count} line(s) ${mode === 'replace' ? 'put in place of the old text' : 'added to the end'} — press Save to keep them.`);
    setUploaded(null);
  };

  const load = useCallback(async () => {
    if (!recipeId) return;
    const res = await ackApi.recipeInstructions(recipeId);
    if (res?.status === 'success') {
      setBlock(res.data);
      setText(res.data.text || '');
      setRows((res.data.abbreviations || []).map((a, i) => ({ ...a, key: i })));
    } else toast.error(res?.message || 'Could not load the general instructions');
  }, [recipeId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => {
    if (!block) return false;
    const cur = JSON.stringify(rows.map(({ abbr, meaning }) => ({ abbr, meaning })).filter((r) => r.abbr || r.meaning));
    return text !== (block.text || '') || cur !== JSON.stringify(block.abbreviations || []);
  }, [block, text, rows]);

  // 2026-10-12 · what is typed here, reported to the form around it (Edit MBMR), so its Update
  // saves unsaved instructions too instead of leaving them behind the tab's own Save.
  useEffect(() => {
    onStateChange?.({ loaded: !!block, dirty, text, abbreviations: rows.map(({ abbr, meaning }) => ({ abbr, meaning })).filter((r) => r.abbr || r.meaning) });
  }, [block, dirty, text, rows]);   // eslint-disable-line react-hooks/exhaustive-deps

  const setCell = (key, field, value) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  const addRow = () => setRows((prev) => [...prev, { key: Date.now(), abbr: '', meaning: '' }]);
  const delRow = (key) => setRows((prev) => prev.filter((r) => r.key !== key));

  const save = async () => {
    // 2026-10-12 · mandatory on an MBMR: an empty block is refused here, with the reason, rather
    // than saved — every executor must read and e-sign these before their first action.
    if (required && !text.trim()) {
      toast.error('General instructions are required — please add them (type them or use Upload .txt) before saving.');
      return;
    }
    setSaving(true);
    const res = await ackApi.saveRecipeInstructions(recipeId, {
      general_instructions: text,
      abbreviations_json: rows.map(({ abbr, meaning }) => ({ abbr, meaning })),
      change_reason: 'MBMR general instructions edited',
    });
    setSaving(false);
    if (res?.status === 'success') {
      toast.success('General instructions saved');
      setBlock(res.data);
      setRows((res.data.abbreviations || []).map((a, i) => ({ ...a, key: i })));
    } else toast.error(res?.message || 'Save failed');
  };

  const copy = async () => {
    const res = await ackApi.copyRecipeInstructions(recipeId, copyTargets);
    if (res?.status === 'success') {
      toast.success(`Copied to ${res.data.copied_to.length} MBMR(s)`);
      setCopyOpen(false);
    } else toast.error(res?.message || 'Copy failed');
  };

  const columns = [
    {
      title: 'Abbreviation',
      dataIndex: 'abbr',
      width: 180,
      sortable: false,
      filterable: false,
      render: (v, r) => (readOnly ? <Text strong>{v}</Text> : <Input size="small" value={v} onChange={(e) => setCell(r.key, 'abbr', e.target.value)} placeholder="e.g. IPQA" />),
    },
    {
      title: 'Meaning',
      dataIndex: 'meaning',
      sortable: false,
      filterable: false,
      render: (v, r) => (readOnly ? v : <Input size="small" value={v} onChange={(e) => setCell(r.key, 'meaning', e.target.value)} placeholder="Meaning as printed on the BMR" />),
    },
    ...(readOnly
      ? []
      : [
          {
            title: '',
            dataIndex: 'key',
            width: 48,
            sortable: false,
            filterable: false,
            exportable: false,
            render: (k) => (
              <Popconfirm title="Remove this abbreviation?" onConfirm={() => delRow(k)}>
                <Button size="small" type="text" danger icon={<Trash2 size={14} />} />
              </Popconfirm>
            ),
          },
        ]),
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <Space wrap>
          {block?.version && <Tag color="blue">version {block.version}</Tag>}
          {block && <Tag>{block.line_count} instruction line(s) · {(block.abbreviations || []).length} abbreviation(s)</Tag>}
          {dirty && <Tag color="gold">unsaved changes</Tag>}
        </Space>
        {!readOnly && (
          <Space>
            {siblingRecipes.length > 0 && (
              <Button icon={<Copy size={14} />} onClick={() => setCopyOpen(true)} disabled={dirty}>
                Copy to other MBMRs
              </Button>
            )}
            <input ref={fileRef} type="file" accept=".txt,text/plain" style={{ display: 'none' }} onChange={onFile} />
            <Tooltip title="Accepted: .txt files only (plain text, UTF-8), up to 200 KB. Each line of the file becomes one instruction; empty lines are skipped.">
              <Button icon={<Upload size={14} />} onClick={pickFile}>
                Upload .txt
              </Button>
            </Tooltip>
            <Text type="secondary" style={{ fontSize: 12.5 }}>Accepted: .txt · up to 200 KB</Text>
            <Button type="primary" icon={<Save size={14} />} loading={saving} disabled={!dirty && !(required && !text.trim())} onClick={save}>
              Save
            </Button>
          </Space>
        )}
      </div>
      {required && block && !String(block.text || '').trim() ? (
        <Alert type="error" showIcon style={{ marginBottom: 10 }}
               message="Required — this MBMR has no general instructions yet. Add them (type or Upload .txt) and press Save; the MBMR cannot be updated without them." />
      ) : null}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 10 }}
        message="Optional. Shown on the tablet before each process of this MBMR, together with that process's own instruction; each person reads it once before starting the process."
      />
      <Tabs
        size="small"
        items={[
          {
            key: 'text',
            label: 'General instructions',
            // 2026-10-12 · a fixed-height box that scrolls inside itself: the instructions grew the
            // whole form, so the dialog scrolled instead of the text.
            children: readOnly ? (
              <div style={{ height: BOX_H, overflowY: 'auto', paddingRight: 6 }} dangerouslySetInnerHTML={renderInstructions(text)} />
            ) : (
              <Input.TextArea
                value={text}
                onChange={(e) => setText(e.target.value)}
                style={{ height: BOX_H, resize: 'none', overflowY: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}
                placeholder={'Markdown allowed — one instruction per line, e.g.\n1. Take area and line clearance from QA prior to start batch activity.\n2. All the critical steps shall be done by (activity performed by) and checked by (witnessed by) second person.'}
              />
            ),
          },
          {
            key: 'preview',
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Eye size={13} /> Preview
              </span>
            ),
            children: <div className="prose max-w-none" style={{ fontSize: 14, height: BOX_H, overflowY: 'auto', paddingRight: 6 }} dangerouslySetInnerHTML={renderInstructions(text)} />,
          },
          {
            key: 'abbr',
            label: `Abbreviations (${rows.length})`,
            children: (
              <div style={{ height: BOX_H, overflowY: 'auto' }}>
              <DataTable
                tableKey="mbmrAbbreviations"
                title="Abbreviations"
                columns={columns}
                data={rows}
                rowKey="key"
                height={null}
                pagination={false}
                searchable
                searchKeys={['abbr', 'meaning']}
                exportable
                exportFilename={`mbmr-${recipeId}-abbreviations`}
                toolbarExtra={
                  !readOnly && (
                    <Button size="small" icon={<Plus size={14} />} onClick={addRow}>
                      Add
                    </Button>
                  )
                }
              />
              </div>
            ),
          },
        ]}
      />
      <Modal open={!!uploaded} title={`Add the instructions from ${uploaded?.name || 'the file'}`} onCancel={() => setUploaded(null)}
             footer={[
               <Button key="cancel" onClick={() => setUploaded(null)}>Cancel</Button>,
               <Button key="append" onClick={() => applyUpload('append')}>Add to the end</Button>,
               <Button key="replace" type="primary" danger onClick={() => applyUpload('replace')}>Replace the current text</Button>,
             ]}>
        <Text>
          The file has {uploaded?.count} instruction line(s). The editor already has text — add the file&apos;s lines after it, or replace it?
          Nothing is saved until you press Save.
        </Text>
      </Modal>
      <Modal open={copyOpen} title="Copy general instructions & abbreviations to" onCancel={() => setCopyOpen(false)} onOk={copy} okButtonProps={{ disabled: !copyTargets.length }}>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select MBMRs"
          value={copyTargets}
          onChange={setCopyTargets}
          options={siblingRecipes.filter((r) => String(r.id) !== String(recipeId)).map((r) => ({ value: r.id, label: `${r.recipe_code || r.id} — ${r.name}` }))}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Overwrites the target MBMRs' text; batches already issued keep their frozen copy.
        </Text>
      </Modal>
    </div>
  );
}

GeneralInstructionsPanel.propTypes = {
  recipeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siblingRecipes: PropTypes.array,
  readOnly: PropTypes.bool,
};
